import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ModalV2, Text, Flex, Textarea, ErrorMessage, Select, Button } from "@jsq/ds/core";
import { CancelButton, Form, SubmitButton } from "@jsq/ds/forms";
import {
  type ComplianceCaseStatus,
  type ComplianceCaseTypeEnum,
  type GetComplianceCaseQuery,
} from "~/graphql/supergraphTypes";
import { showsImpactedGpPreview, showsProfileSelect } from "../escalationCaseTypes";
import {
  type EscalationRecord,
  useEscalationForm,
} from "../EscalationTakeover/useEscalationForm";
import ImpactedGPGrid from "../IssueGrid/ImpactedGPGrid";
import {
  ImpactedGPContext,
  useImpactedGPContext,
} from "../IssueGrid/ImpactedGPGrid/impactedGPcontext";
import {
  EscalationModalInputHeader_Style,
  AdditionalTitle_Style,
  TextareaError_Style,
  ScrollableModalBody_Style,
} from "./styles_vanilla.css";
import { EscalationFormValues } from "./types";

interface Props {
  show: boolean;
  onCloseModal: () => void;
  caseType: ComplianceCaseTypeEnum | undefined;
  profileId: number;
  caseStatus: ComplianceCaseStatus;
  // When provided, the modal will render a profile selector and use the selected profileId.
  profilesForCase?: GetComplianceCaseQuery["complianceCase"] | null | undefined;
  mode?: "create" | "edit";
  escalationToEdit?: EscalationRecord;
}

const CreateEscalationModal: React.FC<Props> = ({
  show,
  onCloseModal,
  caseType,
  profileId,
  caseStatus,
  profilesForCase,
  mode = "create",
  escalationToEdit,
}) => {
  const parentCtx = useImpactedGPContext();
  const [count, setCount] = useState<number>(0);
  const shouldUseInternalProvider =
    !parentCtx || (parentCtx.setCount === undefined && parentCtx.count === 0);
  const effectiveCount = shouldUseInternalProvider ? count : parentCtx.count;
  const displayCount = effectiveCount;
  const INITIAL_VALUES: EscalationFormValues = {
    issue: "",
  };
  const location = useLocation();
  const parts = location.pathname.split("/");
  const caseId = Number(parts[3]);

  const form = useEscalationForm({
    caseId,
    caseType,
    profileId,
    profilesForCase,
    mode,
    escalationToEdit,
    onDone: onCloseModal,
  });

  // Reset impacted GP count whenever profile selection changes so the notice refreshes
  useEffect(() => {
    setCount(0);
  }, [form.selectedProfileId]);

  const showProfileSelectForEscalation = showsProfileSelect(caseType);
  const showImpactedGpGrid = showsImpactedGpPreview(caseType);

  const modalBody = (
    <ModalV2
      show={show}
      isCentered={false}
      onHide={onCloseModal}
      data-testid="create-escalation-modal"
      title={mode === "edit" ? "Edit escalation" : "Create escalation"}
    >
      <Form<EscalationFormValues>
        initialValues={INITIAL_VALUES}
        onSubmit={form.submitCreate}
        submitOnlyChangedValues={false}
      >
        <ModalV2.Body className={ScrollableModalBody_Style}>
          <Flex flexDirection="column">
            {profilesForCase && showProfileSelectForEscalation && (
              <>
                <Text className={EscalationModalInputHeader_Style} variant="strong-md">
                  Profile
                </Text>
                <Select
                  placeholder="Select a profile"
                  value={form.selectedProfileId}
                  onChange={(value) => form.setSelectedProfileId(Number(value))}
                  full
                >
                  <Select.Section>
                    {form.profileOptions.map((opt) => (
                      <Select.Option value={opt.value} key={opt.value}>
                        {opt.label}
                      </Select.Option>
                    ))}
                  </Select.Section>
                </Select>
                {form.hasDuplicateEscalationForSelectedProfile && form.selectedProfileName && (
                  <ErrorMessage>
                    {`There is already a proposed or outstanding escalation for ${form.selectedProfileName}. A profile can only have one proposed or outstanding escalation per originating case.`}
                  </ErrorMessage>
                )}
              </>
            )}
            <Text className={EscalationModalInputHeader_Style} variant="strong-md">
              Issue
            </Text>
            <Textarea
              name="issue"
              placeholder="Describe the reason for escalating the selected profile"
              value={form.issueInput}
              isFullWidth
              className={form.errorVisible ? TextareaError_Style : undefined}
              onSetValue={form.setIssue}
            ></Textarea>
            {form.errorVisible && <ErrorMessage>Required</ErrorMessage>}
            {showImpactedGpGrid && (
              <Text variant="strong-md" className={AdditionalTitle_Style}>
                {displayCount
                  ? `Additional escalation cases will be created for ${displayCount} ${displayCount > 1 ? "GPs" : "GP"}`
                  : ""}
              </Text>
            )}
            {caseType && showImpactedGpGrid && form.effectiveProfileId != null && (
              <ImpactedGPGrid
                caseId={caseId}
                caseType={caseType}
                profileId={form.effectiveProfileId}
                caseStatus={caseStatus}
              />
            )}
          </Flex>
        </ModalV2.Body>
        <ModalV2.Footer>
          {mode === "edit" ? (
            <Flex justifyContent="flex-end" gap="12px">
              <Button
                variant="danger"
                onClick={form.removeEscalation}
                isLoading={form.deleteLoading}
                data-testid="delete-escalation-button"
              >
                Delete Escalation
              </Button>
              <Button
                variant="primary"
                color="green"
                onClick={form.saveEdit}
                isLoading={form.updateLoading}
                data-testid="save-escalation-button"
                disabled={form.hasDuplicateEscalationForSelectedProfile}
              >
                Save
              </Button>
            </Flex>
          ) : (
            <Flex justifyContent="flex-end" gap="12px">
              <CancelButton data-testid="cancel-button" onClick={onCloseModal} />
              <SubmitButton
                data-testid="create-button"
                isLoading={form.createLoading}
                disabled={form.hasDuplicateEscalationForSelectedProfile}
              >
                Create
              </SubmitButton>
            </Flex>
          )}
        </ModalV2.Footer>
      </Form>
    </ModalV2>
  );
  if (shouldUseInternalProvider) {
    return (
      <ImpactedGPContext.Provider value={{ count, setCount }}>
        {modalBody}
      </ImpactedGPContext.Provider>
    );
  }
  return modalBody;
};
export default CreateEscalationModal;
